import { Card } from 'react-bootstrap';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import { IndexerKpiCard } from '../IndexerKpiCard';

/** بلوک shimmer هم‌رنگ SERP (`TextLoading` + changeBgColor). */
function Block({
  width,
  height,
  className = '',
  roundedCircle = false,
}: {
  width: number | string;
  height: number;
  className?: string;
  roundedCircle?: boolean;
}) {
  const isPx = typeof width === 'number';
  return (
    <span
      className={`d-block ${roundedCircle ? 'rounded-circle overflow-hidden' : ''} ${className}`.trim()}
      style={
        roundedCircle && isPx
          ? { width, height, borderRadius: '50%' }
          : undefined
      }
    >
      <TextLoading width={width} height={height} isWidthPx={isPx} />
    </span>
  );
}

/** KPI cards matching `.overview-kpi-grid` layouts. */
export function IndexerKpiGridPreload({
  count = 6,
  gridClassName = '',
}: {
  count?: number;
  gridClassName?: string;
}) {
  return (
    <div
      className={`overview-kpi-grid ${gridClassName}`.trim()}
      aria-hidden
    >
      {Array.from({ length: count }, (_, index) => (
        <IndexerKpiCard key={index} tone="neutral" imageIndex={index}>
          <Block width={72} height={22} className="mb-2" />
          <Block width="55%" height={12} />
        </IndexerKpiCard>
      ))}
    </div>
  );
}

/** AG Grid / list rows inside a card body. */
export function IndexerTablePreload({
  rows = 8,
  cols = 4,
}: {
  rows?: number;
  cols?: number;
}) {
  const widths = ['28%', '18%', '22%', '14%', '16%', '20%'];
  return (
    <div className="px-2 py-2" aria-hidden>
      <div className="d-flex gap-2 border-bottom border-200 pb-2 mb-2">
        {Array.from({ length: cols }, (_, index) => (
          <span key={`h-${index}`} className="flex-grow-1">
            <Block width={widths[index % widths.length]} height={12} />
          </span>
        ))}
      </div>
      {Array.from({ length: rows }, (_, row) => (
        <div
          key={row}
          className="d-flex align-items-center gap-2 py-2 border-bottom border-200"
          style={{ minHeight: 48 }}
        >
          {Array.from({ length: cols }, (_, col) => (
            <span key={col} className="flex-grow-1">
              <Block
                width={widths[(row + col) % widths.length]}
                height={11}
              />
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function ChartCardSkeleton({
  bodyHeight = 220,
}: {
  bodyHeight?: number;
}) {
  return (
    <Card className="mb-0 overview-chart-card">
      <Card.Header className="bg-body py-2">
        <Block width="42%" height={14} className="mb-2" />
        <Block width="68%" height={10} />
      </Card.Header>
      <Card.Body>
        <Block width="100%" height={bodyHeight} />
      </Card.Body>
    </Card>
  );
}

/** نمای کلی — KPI + charts / checklist shape. */
export function OverviewPagePreload() {
  return (
    <div className="d-flex flex-column gap-3" aria-hidden>
      <IndexerKpiGridPreload count={6} />
      <div className="row g-3">
        <div className="col-lg-5">
          <ChartCardSkeleton bodyHeight={240} />
        </div>
        <div className="col-lg-7">
          <ChartCardSkeleton bodyHeight={240} />
        </div>
      </div>
      <div className="row g-3">
        <div className="col-lg-6">
          <ChartCardSkeleton bodyHeight={180} />
        </div>
        <div className="col-lg-6">
          <Card className="mb-0">
            <Card.Header className="bg-body py-2">
              <Block width="48%" height={14} className="mb-2" />
              <Block width="70%" height={10} />
            </Card.Header>
            <Card.Body className="d-flex flex-column gap-2">
              {Array.from({ length: 4 }, (_, index) => (
                <div
                  key={index}
                  className="d-flex align-items-start gap-2 p-2 border rounded-3"
                >
                  <Block
                    width={18}
                    height={18}
                    className="flex-shrink-0"
                    roundedCircle
                  />
                  <div className="flex-grow-1">
                    <Block width="70%" height={12} className="mb-2" />
                    <Block width="90%" height={10} />
                  </div>
                  <Block width={52} height={22} className="flex-shrink-0" />
                </div>
              ))}
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}

/** درخواست / وضعیت ایندکس — KPI + form card + history table. */
export function WorkspacePagePreload() {
  return (
    <div className="d-flex flex-column gap-3" aria-hidden>
      <IndexerKpiGridPreload count={6} gridClassName="overview-kpi-grid--auto" />
      <Card className="mb-0">
        <Card.Header className="bg-body py-2">
          <Block width="30%" height={14} className="mb-2" />
          <Block width="55%" height={10} />
        </Card.Header>
        <Card.Body>
          <Block width="100%" height={120} className="mb-3" />
          <div className="d-flex justify-content-end">
            <Block width={120} height={32} />
          </div>
        </Card.Body>
      </Card>
      <Card className="mb-0">
        <Card.Header className="bg-body py-2">
          <Block width="36%" height={14} />
        </Card.Header>
        <Card.Body className="p-0">
          <IndexerTablePreload rows={6} cols={5} />
        </Card.Body>
      </Card>
    </div>
  );
}

/** لیست آدرس‌ها — KPI + toolbar + table. */
export function UrlListPagePreload() {
  return (
    <div className="d-flex flex-column gap-3" aria-hidden>
      <IndexerKpiGridPreload count={6} />
      <Card className="mb-0">
        <Card.Header className="bg-body px-2 py-2">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Block width={220} height={30} />
            <Block width={88} height={30} />
            <Block width={100} height={30} />
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <IndexerTablePreload rows={10} cols={5} />
        </Card.Body>
      </Card>
    </div>
  );
}

/** تغییرات — KPI + search + change cards. */
export function UrlChangesPagePreload() {
  return (
    <div className="d-flex flex-column gap-3" aria-hidden>
      <IndexerKpiGridPreload count={5} gridClassName="overview-kpi-grid--5" />
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <Block width={280} height={30} />
        <Block width={140} height={12} />
      </div>
      {Array.from({ length: 5 }, (_, index) => (
        <Card key={index} className="mb-0">
          <Card.Body className="d-flex align-items-center gap-3 py-3">
            <Block width={96} height={12} className="flex-shrink-0" />
            <div className="flex-grow-1 min-w-0">
              <Block width="78%" height={12} className="mb-2" />
              <Block width="42%" height={10} />
            </div>
            <Block width={72} height={22} className="flex-shrink-0" />
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}

/** تنظیمات — card panels. */
export function SettingsPagePreload({ cards = 2 }: { cards?: number }) {
  return (
    <div className="d-flex flex-column gap-3" aria-hidden>
      {Array.from({ length: cards }, (_, index) => (
        <Card key={index} className="mb-0">
          <Card.Header className="bg-body py-2">
            <Block width="40%" height={14} />
          </Card.Header>
          <Card.Body className="d-flex flex-column gap-2">
            <Block width="92%" height={11} />
            <Block width="78%" height={11} />
            <Block width="64%" height={11} className="mb-2" />
            <Block width={160} height={34} />
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}

/** خلاصه تنظیمات داخل کارت نمای کلی. */
export function OverviewSettingsChecklistPreload() {
  return (
    <div aria-hidden>
      <Block width="45%" height={12} className="mb-2" />
      <Block width="100%" height={6} className="mb-3" />
      <div className="d-flex flex-column gap-2">
        {Array.from({ length: 4 }, (_, index) => (
          <div
            key={index}
            className="d-flex align-items-start gap-2 p-2 border rounded-3"
          >
            <Block
              width={18}
              height={18}
              className="flex-shrink-0"
              roundedCircle
            />
            <div className="flex-grow-1">
              <Block width="72%" height={12} className="mb-2" />
              <Block width="88%" height={10} />
            </div>
            <Block width={52} height={22} className="flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** نمای کلی — تب مقایسه: hero + جداول + نمودارها. */
export function OverviewComparisonPreload() {
  return (
    <div className="d-flex flex-column gap-3 overview-tab-panel" aria-hidden>
      <Block width="48%" height={12} />
      <div className="comparison-hero-grid">
        {Array.from({ length: 3 }, (_, index) => (
          <Card key={index} className="mb-0 comparison-hero-card">
            <Card.Body className="py-3">
              <Block width="62%" height={11} className="mb-3" />
              <div className="d-flex align-items-end justify-content-between gap-2">
                <Block width={64} height={22} />
                <Block width={48} height={18} />
              </div>
              <Block width="40%" height={10} className="mt-2" />
            </Card.Body>
          </Card>
        ))}
      </div>
      <div className="row g-3">
        <div className="col-lg-6">
          <Card className="mb-0 overview-chart-card h-100">
            <Card.Header className="bg-body py-2">
              <Block width="46%" height={14} className="mb-2" />
              <Block width="72%" height={10} />
            </Card.Header>
            <Card.Body className="p-0">
              <IndexerTablePreload rows={3} cols={4} />
            </Card.Body>
          </Card>
        </div>
        <div className="col-lg-6">
          <Card className="mb-0 overview-chart-card h-100">
            <Card.Header className="bg-body py-2">
              <Block width="52%" height={14} className="mb-2" />
              <Block width="68%" height={10} />
            </Card.Header>
            <Card.Body className="p-0">
              <IndexerTablePreload rows={3} cols={4} />
            </Card.Body>
          </Card>
        </div>
      </div>
      <ChartCardSkeleton bodyHeight={280} />
      <div className="row g-3">
        <div className="col-lg-6">
          <ChartCardSkeleton bodyHeight={200} />
        </div>
        <div className="col-lg-6">
          <ChartCardSkeleton bodyHeight={200} />
        </div>
      </div>
    </div>
  );
}

/** وضعیت ایندکس — تب نمودارها: دو نمودار ردیفی + سه کارت پایین. */
export function InspectChartsPreload() {
  return (
    <div className="d-flex flex-column gap-3" aria-hidden>
      <div className="row g-3">
        {Array.from({ length: 2 }, (_, index) => (
          <div key={index} className="col-lg-6 min-w-0">
            <ChartCardSkeleton bodyHeight={280} />
          </div>
        ))}
      </div>
      <div className="row g-3">
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className="col-lg-4 col-md-6 min-w-0">
            <ChartCardSkeleton bodyHeight={220} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** تب زمان‌بندی — هشدار info + نوار ابزار + جدول زمان‌بندی‌ها (داخل بدنهٔ کارت). */
export function SchedulePanelPreload() {
  return (
    <div aria-hidden>
      <Block width="100%" height={44} className="mb-3" />
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <Block width={90} height={12} />
        <Block width={120} height={30} />
      </div>
      <div className="border rounded-3">
        <IndexerTablePreload rows={3} cols={4} />
      </div>
    </div>
  );
}

/** محتوای تب بر اساس مسیر فعلی. */
export function IndexerRoutePreload({ pathname }: { pathname: string }) {
  if (pathname.includes('/indexer/index/')) return <WorkspacePagePreload />;
  if (pathname.includes('/indexer/url-inspection/')) {
    return <WorkspacePagePreload />;
  }
  if (pathname.includes('/indexer/urls/')) return <UrlListPagePreload />;
  if (pathname.includes('/indexer/changes/')) return <UrlChangesPagePreload />;
  if (pathname.includes('/indexer/settings/')) {
    return <SettingsPagePreload />;
  }
  return <OverviewPagePreload />;
}

/**
 * شل ایندکسر هنگام لود پروژه / وضعیت راه‌اندازی —
 * جایگزین اسپینر دایره‌ای تا دوباره‌کاری با preload صفحه نشود.
 */
export function IndexerShellPreload({ pathname }: { pathname: string }) {
  const labels = [
    'نمای کلی',
    'درخواست ایندکس',
    'وضعیت ایندکس',
    'لیست آدرس‌ها',
    'تغییرات',
    'تنظیمات',
  ];

  return (
    <div className="indexer-panel-root" aria-hidden>
      <div className="mb-2">
        <Block width={180} height={14} />
      </div>
      <div className="my-3 border-bottom border-1">
        <div className="d-flex overflow-auto flex-nowrap">
          {labels.map((label) => (
            <div
              key={label}
              className="py-2 pt-3"
              style={{ padding: '0 1.7rem' }}
            >
              <Block width={label.length * 7} height={12} />
            </div>
          ))}
        </div>
      </div>
      <IndexerRoutePreload pathname={pathname} />
    </div>
  );
}
